import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, isGTMAvailable, isGtagAvailable } from "../util";

const usePageTrackingWithObserver = () => {
  const location = useLocation();
  const timeoutRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // Early return if no analytics services are available
    if (!isGTMAvailable() && !isGtagAvailable()) {
      console.debug('No analytics services available - skipping page tracking');
      return;
    }

    // Clean up previous observers/timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    let hasTracked = false;

    const trackPage = () => {
      if (hasTracked) return;
      hasTracked = true;

      const fullPath = location.pathname + location.search + location.hash;
      const fullUrl = window.location.origin + fullPath;

      trackPageView({
        title: document.title,
        location: fullUrl,
        path: fullPath,
      });
    };

    // Watch for title changes
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.target.nodeName === 'TITLE') {
          setTimeout(trackPage, 50);
        }
      });
    });

    // Start observing title changes
    const titleElement = document.querySelector('title');
    if (titleElement) {
      observerRef.current.observe(titleElement, { childList: true });
    }

    // Fallback timeout in case title doesn't change
    timeoutRef.current = setTimeout(trackPage, 500);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [location]);
};

export default usePageTrackingWithObserver;
